import React, { useState, useEffect, useRef } from "react";
import { useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Grid, FormLabel, TextField, Checkbox, Collapse, CircularProgress } from "@mui/material";
import { useSelector } from "react-redux";
import { CommonSelect } from "../common";
import { CommonButton } from "../common/commonButton";
import instance from "../../redux/api";
import { getAllPaymentIds, getPayoutSummary } from "../../Services/api";
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import { numberFormatter } from '../../utils/generalFunctions'
import "./bdx.css";
import MUIDataTable from "mui-datatables";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Paper from "@material-ui/core/Paper";
import { format } from 'date-fns';
import Tooltip from '@mui/material/Tooltip';
import FilterAltOffIcon from '@mui/icons-material/FilterAltOff';
import SearchIcon from '@mui/icons-material/Search';
import { commonToast } from "../common";

function handleAmount(val) {
  const options = {
    style: "decimal",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  };

  return (
    (Number(val) && Number(val).toLocaleString("en-US", options)) ||
    (val === 0 && parseInt(val).toFixed(2)) ||
    val
  );
}

const CoversheetList = props => {
  const { search } = useLocation();
  const coverholderName = new URLSearchParams(search).get("coverholder_name");
  const settlementCurrency = new URLSearchParams(search).get("settlementCurrency");
  const paymentType = new URLSearchParams(search).get("paymentType");

  const [paymentIds, setPaymentIds] = useState([]);
  const [isSearchPerformed, setIsSearchPerformed] = useState(false);
  const [isClearPerformed, setIsClearPerformed] = useState(false);
  const [currencies, setCurrencies] = useState([]);
  const [isFilter, setIsFilter] = useState(false);
  const [transformedData, setTransformedData] = useState([]);
  const [loading, setLoading] = useState(true);

  const [searchFieldParams, setSearchFieldsParams] = useState({
    type_of_payment: paymentType,
    coverholder_name: coverholderName,
    settlement_currency: settlementCurrency,
    payment_id: "",
  });

  const toggle = useSelector((state) => state.toggleSideMenu);

  const getCurrencies = async () => {
    instance
      .get(`documents/currency_details/`)
      .then((response) => setCurrencies(response?.data ?? []))
      .catch((error) =>
        console.error("Error fetching dropdown values:", error)
      );
  }

  const [pageState, setPageState] = useState({
    isLoading: false,
    data: [],
    total: 0,
    page: 1,
    pageSize: 25,
  });

  const Container = {
    width: toggle?.isOpen ? "calc(100vw - 305px)" : "calc(100vw - 100px)",
    display: "flex",
    flexDirection: "column",
    gap: 8,
  };

  const paymentTypes = [
    "Syndicate Payment",
    "Internal Broking",
    "MGA Commission",
    "Rebate",
  ];

  const paymentStatus = [
    {label: "Paid", value: 'paid'},
    {label: "Not Paid", value: 'unpaid'},
    {label: "Pending", value: 'pending'}
  ];

  const getPaymentIds = async () => {
    const response = await instance.get(`${getAllPaymentIds}`);
    setPaymentIds(response?.data?.data);
  };

  const searchQueryParams = () => {
    const { type_of_payment, coverholder_name, settlement_currency, payment_id } = searchFieldParams;
    let queryParams = [];
  
    if (type_of_payment) queryParams.push(`type_of_payment=${type_of_payment}`);
    if (coverholder_name) queryParams.push(`coverholder_name=${coverholder_name}`);
    if (settlement_currency) queryParams.push(`settlement_currency=${settlement_currency}`);
    if (payment_id) queryParams.push(`payment_id=${payment_id}`);
  
    return queryParams.length ? `&${queryParams.join('&')}` : '';
  };

  const loadData = async (page = 1, pageSize = 25, isPageSizeChange = false) => {
    setLoading(true);
    setPageState((old) => ({
      ...old,
      isLoading: true,
    }));
    try {
      const queryParams = `skip=${(page - 1) * pageSize}&pageSize=${pageSize}&payment_status=true`;
      let response;
      if (isSearchPerformed) {
        const getSearchQueryParam = searchQueryParams();
        response = await instance.get(`${getPayoutSummary}?${queryParams}${getSearchQueryParam}`);
      } else {
        response = await instance.get(`${getPayoutSummary}?${queryParams}`);
      }
      setPageState((old) => ({
        ...old,
        isLoading: false,
        data: response?.data?.data,
        total: response?.data?.total,
      }));

      const columnKeys = columns.map(column => column.field);
      const transformedData = response?.data?.data.map(item => 
        columnKeys.map(key => item[key] !== undefined ? item[key] : null)
      );
      setTransformedData(transformedData);
    } catch (err) {
      setPageState((old) => ({
        ...old,
        isLoading: false,
      }));
      console.error("err", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectDropdown = (name, value, reason) => {
    if (reason === "clear") {
      loadData();
    }
    setSearchFieldsParams({
      ...searchFieldParams,
      [name]: value ?? "",
    });
  };

  const handleClearSearchList = async () => {
    setIsClearPerformed(true);
    setIsSearchPerformed(false);

    const clearedFields = {
      type_of_payment: "",
      coverholder_name: "",
      settlement_currency: "",
      payment_id: "",
    };
    // Set the cleared fields using a functional update
    setSearchFieldsParams((prev) => ({
      ...prev,
      ...clearedFields,
    }));

    setIsFilter(false);
  };

  // Add these state variables near other useState declarations
  const [openReleaseModal, setOpenReleaseModal] = useState(false);
  const [releasePaymentData, setReleasePaymentData] = useState({
    payment_complete_status: '',
    wire_ref: '',
    payment_date: ''
  });
  const [isButtonEnable, setIsButtonEnable] = useState(false);

  // Add these handler functions
  const handleReleasePaymentClick = () => {
    setOpenReleaseModal(true);
  };

  const handleCloseReleaseModal = () => {
    setOpenReleaseModal(false);
    setReleasePaymentData({
      payment_complete_status: '',
      wire_ref: '',
      payment_date: ''
    });
  };

  useEffect(() => {
    setIsButtonEnable(releasePaymentData.payment_complete_status === "" || releasePaymentData.wire_ref === "" || releasePaymentData.payment_date === "")
  }, [releasePaymentData])

  const handleReleasePaymentSubmit = async () => {
    try {
      // Modify the releasePaymentData to only send the label for payment_complete_status
      const payloadData = {
        ...releasePaymentData,
        payment_complete_status: releasePaymentData.payment_complete_status?.label || releasePaymentData.payment_complete_status
      };
      console.log(getPayoutSummary, selectedRecord, releasePaymentData)
      // Add your API call here to save the payment data
      const response = await instance.patch(
        `${getPayoutSummary}${selectedRecord}/`,
        payloadData
      );

      if (response) {
        commonToast("success", 'Payment released successfully!');
        handleCloseReleaseModal();
        loadData(); // Refresh the grid
      }
    } catch (err) {
      console.error(err);
      commonToast("error", 'Unable to release payment');
    }
  };

  const [selectedRecord, setSelectedRecord] = useState(null);

  const handleCheckboxChange = (checked, rowId) => {
    console.log("Checkbox checked:", checked);
    console.log("Row ID:", rowId);
    if (checked) {
      setSelectedRecord(rowId);
    } else {
      setSelectedRecord(null);
    }
  };


  const renderExpandableRow = (rowData, rowMeta) => {
    const rowId = rowData[0]; // Assuming the first column is the unique ID
    const bdxIds = pageState.data.find(row => row.id === rowId)?.bdx_ids || {};
    
    return (
      <TableRow>
        <TableCell colSpan={columns.length}>
          <Collapse in={true} timeout="auto" unmountOnExit>
            <TableContainer style={{ width: '45%' }} component={Paper} className="collapse-table-container">
              <Table aria-label="simple table">
                <TableHead>
                  <TableRow>
                    <TableCell>ID</TableCell>
                    <TableCell>Policy No</TableCell>
                    <TableCell>Final Net Premium SC</TableCell>
                    <TableCell>Rebate</TableCell>
                    <TableCell>Net Payment</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {bdxIds.map((data, index) => (
                    <TableRow key={index}>
                      <TableCell>{data.id}</TableCell>
                      <TableCell>{data.policy_no}</TableCell>
                      <TableCell>{data.finalnetpremiumsc}</TableCell>
                      <TableCell>{data.rebate}</TableCell>
                      <TableCell>{data.net_payment}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Collapse>
        </TableCell>
      </TableRow>
    );
  };

  const options = {
    filter: true,
    onFilterChange: (changedColumn, filterList) => {
      console.log(changedColumn, filterList);
    },
    selectableRows: "none", // Disable default selectable rows
    filterType: "dropdown",
    rowsPerPage: pageState.pageSize,
    count: pageState.total,
    page: pageState.page - 1,
    expandableRows: true,
    expandableRowsHeader: false,
    download: false,
    print: false,
    search: false,
    filter: false,
    viewColumns: false,
    serverSide: true,
    selectToolbarPlacement: 'none',
    onTableChange: (action, tableState) => {
      switch (action) {
        case 'changePage':
          loadData(tableState.page + 1, pageState.pageSize);
          break;
        case 'changeRowsPerPage':
          loadData(1, tableState.rowsPerPage, true);
          break;
        default:
          break;
      }
    },
    renderExpandableRow: (rowData, rowMeta) => renderExpandableRow(rowData, rowMeta),
    rowsPerPageOptions: [10, 25, 50, 100], // Set rows per page options
    customToolbarSelect: () => {
      return pageState.isLoading ? <CircularProgress size={24} /> : null;
    },
    textLabels: {
      body: {
        noMatch: pageState.isLoading ? <CircularProgress size={24} /> : "Sorry, no data found",
      },
    },
  };

  const columns = [
    {
      field: "id",
      name: "ID",
      options: {
        display: false, // Hide the ID column
      },
    },
    {
      name: "Select",
      options: {
        customBodyRender: (value, tableMeta, updateValue) => {
          const rowId = tableMeta.rowData[0];
          const isChecked = selectedRecord === rowId;
          return (
            <div style={{ textAlign: 'center' }}>
              <Checkbox
                checked={isChecked}
                onChange={(e) => handleCheckboxChange(e.target.checked, rowId)}
                style={{ color: tableMeta.rowData[10] === "paid" ? "gray" : "#FF5A01" }}
                disabled={tableMeta.rowData[10] === "paid"}
              />
            </div>
          );
        },
      },
    },
    {
      field: "payment_id",
      name: "Payment ID",
      headerAlign: "center",
      width: 160,
      options: {
        customBodyRender: (value) => (
          <div style={{ textAlign: 'center' }}>{value}</div>
        ),
      },
    },
    {
      field: "payment_type",
      name: "Payment Type",
      headerAlign: "center",
      width: 160,
      options: {
        customBodyRender: (value) => (
          <div style={{ textAlign: 'center' }}>{value}</div>
        ),
      },
    },
    {
      field: "coverholder_name",
      name: "Coverholder Name",
      headerAlign: "center",
      width: 300,
      options: {
        customBodyRender: (value) => (
          <div style={{ textAlign: 'center' }}>{value}</div>
        ),
      },
    },
    {
      field: "final_bank_account",
      name: "Final Bank Account",
      headerAlign: "center",
      width: 200,
    },
    {
      field: "final_bank_name",
      name: "Final Bank Name",
      headerAlign: "center",
      width: 160,
      options: {
        customBodyRender: (value) => (
          <div style={{ textAlign: 'center' }}>{value}</div>
        ),
      },
    },
    {
      field: "to_bank_account_name",
      name: "To Bank Account Name",
      headerAlign: "center",
      width: 160,
      options: {
        customBodyRender: (value) => (
          <div style={{ textAlign: 'center' }}>{value}</div>
        ),
      },
    },
    {
      field: "to_bank_account",
      name: "To Bank Account",
      headerAlign: "center",
      width: 160,
    },
    {
      field: "settlement_currency",
      name: "Settlement Ccy",
      headerAlign: "center",
      width: 160,
      options: {
        customBodyRender: (value) => (
          <div style={{ textAlign: 'center' }}>{value}</div>
        ),
      },
    },
    {
      field: "sum_of_rebate",
      name: "Rebate",
      headerAlign: "center",
      width: 160,
      valueFormatter: (params) => {
        return numberFormatter(params.value);
      },
      options: {
        customBodyRender: (value) => (
          <div style={{ textAlign: 'right' }}>{value}</div>
        ),
      },
    },
    {
      field: "sum_of_net_payment",
      name: `Total: ${pageState?.data.reduce((acc, row) => acc + parseFloat(row.sum_of_net_payment || 0), 0).toLocaleString()}`,
      headerAlign: "center",
      width: 160,
      options: {
        customBodyRender: (value) => (
          <div style={{ textAlign: 'right' }}>{handleAmount(value)}</div>
        ),
      },
    },
    {
      field: "payment_date",
      name: "Payment Date",
      headerAlign: "center",
      width: 160,
      options: {
        customBodyRender: (value) => {
          const formattedDate = value ? format(new Date(value), 'dd-MM-yyyy') : '';
          return <div style={{ textAlign: 'center' }}>{formattedDate}</div>;
        },
      },
    },
    {
      field: "payment_complete_status",
      name: "Payment Status",
      headerAlign: "center",
      width: 180,
      options: {
        customBodyRender: (value) => (
          <div style={{ textAlign: 'center' }}>{value}</div>
        ),
      },
    },
    {
      field: "wire_ref",
      name: "Wire #",
      headerAlign: "center",
      width: 160,
      options: {
        customBodyRender: (value) => (
          <div style={{ textAlign: 'center' }}>{value}</div>
        ),
      },
    },
  ];

  const handleFieldsInputChange = (name, value, reason) => {
    if (!value) {
      handleClearSearchList();
    }
    setSearchFieldsParams({
      ...searchFieldParams,
      [name]: value?.trim(),
    });
  };

  const initialLoad = useRef(true);

  useEffect(() => {
    if (initialLoad.current) {
      initialLoad.current = false;
      loadData();
      getPaymentIds();
      getCurrencies();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageState?.pageSize, pageState?.total, pageState?.page]);

  useEffect(() => {
    if (isSearchPerformed) {
      loadData();
    }
  }, [isSearchPerformed]); // This useEffect will trigger loadData when isSearchPerformed changes

  useEffect(() => {
    if (isClearPerformed) {
      loadData();
      setIsClearPerformed(false); // Reset the flag after loading data
    }
  }, [isSearchPerformed, searchFieldParams]); // Include searchFieldParams in dependencies

  const handleEnter = (e) => {
    if (e.code === "Enter") {
      setIsFilter(true);
      setIsSearchPerformed(true);
      loadData();
    }
  };

  return (
    <>
      <div style={Container}>
       <span
         style={{
           color: "#FF5A01",
           fontSize: "20px",
           fontWeight: "400",
           marginTop: 5,
         }}
       >
         Payout Summary
       </span>
       <Grid
         sx={{
           display: "flex",
           justifyContent: "flex-start",
           alignItems: "center",
           mb: "5px",
         }}
         container
         xs={12}
         gap={"10px"}
       >
         <Grid item xs={12} md={2} paddingRight={1}>
           <FormLabel>Payment Type</FormLabel>
           <div onKeyUp={(e) => handleEnter(e)}>
           <CommonSelect
             placeholder="Select Payment Type"
             value={searchFieldParams?.type_of_payment}
             handleChange={(event, value, reason) =>
               handleSelectDropdown("type_of_payment", value, reason, event)
             }
             options={paymentTypes?.map((option) => option) || []}
           />
           </div>
         </Grid>
         <Grid
           item
           display={"flex"}
           flexDirection={"column"}
           gap={"2px"}
           xs={12}
           md={2}
         >
           <FormLabel>Coverholder Name</FormLabel>
           <TextField
             value={searchFieldParams?.coverholder_name}
             onChange={(e) =>
               handleFieldsInputChange("coverholder_name", e.target.value)
             }
             size="small"
             onKeyUp={(e) => handleEnter(e)}
           />
         </Grid>
         <Grid
           item
           display={"flex"}
           flexDirection={"column"}
           gap={"2px"}
           xs={12}
           md={2}
         >
           <FormLabel>Settlement Currency</FormLabel>
            <div onKeyUp={(e) => handleEnter(e)}>
            <CommonSelect
                placeholder="Select Settlement Currency"
                name="settlement_currency"
                value={searchFieldParams?.settlement_currency}
                handleChange={(event, value, reason) =>
                  handleSelectDropdown(
                    "settlement_currency",
                    value,
                    reason,
                    event
                  )
                }
                options={
                  currencies?.map((option) => option?.currency_code) ||
                  []
                }
              />
            </div>
         </Grid>
         <Grid item xs={12} md={2} paddingRight={1}>
           <FormLabel>Payment ID</FormLabel>
           <div onKeyUp={(e) => handleEnter(e)}>
           <CommonSelect
             placeholder="Select Payment Id"
             value={searchFieldParams?.payment_id}
             handleChange={(event, value, reason) =>
               handleSelectDropdown("payment_id", value, reason, event)
             }
             options={paymentIds?.map((option) => option) || []}
           />
           </div>
         </Grid>
          <Grid
            style={{ paddingLeft: '5%', paddingTop: "30px" }}
          >
            <FormLabel></FormLabel>
            <Tooltip title="Apply Filter">
              <SearchIcon
                style={{ cursor: "pointer", color: "#FF5A01" }}
                onClick={() => {
                  setIsFilter(true);
                  setIsSearchPerformed(true); // Indicate that a search has been performed
                }}
              />
            </Tooltip>
          </Grid>
          <Grid
            style={{paddingTop: "30px" }}
          >
            <FormLabel></FormLabel>
            <Tooltip title="Clear Filter">
              <FilterAltOffIcon
                style={{ cursor: "pointer", color: "#FF5A01" }}
                onClick={() => {
                  setIsFilter(false);
                  setIsClearPerformed(true);
                  handleClearSearchList();
                }}
              />
            </Tooltip>
          </Grid>
           <Grid
            style={{ paddingTop: "20px" }}
           >
             <FormLabel></FormLabel>
             <CommonButton
             style={{textWrapMode: "nowrap"}}
               text="Payment Confirm"
               className="bankStatementInitiateBtn"
               hoverColor="#EE3F00"
               disabled={!selectedRecord}
               handleClick={handleReleasePaymentClick}
             />
           </Grid>
         </Grid>
      </div>
      <>
        {loading ? (
          <CircularProgress
            style={{
              position: "absolute",
              top: "70%",
              left: "50%",
            }}
          />
        ) : (
         transformedData && (
          <MUIDataTable
            className="table_row"
            data={transformedData}
            columns={columns}
            options={options}
          /> )
        )}
      </>
      <Dialog open={openReleaseModal} onClose={handleCloseReleaseModal}>
        <DialogTitle>Release Payment</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <FormLabel>Payment Status</FormLabel>
              <CommonSelect
                placeholder="Select Payment Status"
                value={releasePaymentData.payment_complete_status}
                handleChange={(event, value) =>
                  setReleasePaymentData(prev => ({ ...prev, payment_complete_status: value.value }))
                }
                options={paymentStatus}
                getOptionLabel={(option) => option.label}
              />
            </Grid>
            <Grid item xs={12}>
              <FormLabel>Wire #</FormLabel>
              <TextField
                fullWidth
                value={releasePaymentData.wire_ref}
                onChange={(e) =>
                  setReleasePaymentData(prev => ({ ...prev, wire_ref: e.target.value }))
                }
                size="small"
                disabled={releasePaymentData.payment_complete_status != 'paid'}
              />
            </Grid>
            <Grid item xs={12}>
              <FormLabel>Payment Date</FormLabel>
              <TextField
                fullWidth
                type="date"
                value={releasePaymentData.payment_date}
                onChange={(e) =>
                  setReleasePaymentData(prev => ({ ...prev, payment_date: e.target.value }))
                }
                size="small"
                disabled={releasePaymentData.payment_complete_status != 'paid'}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <CommonButton
            text="Cancel"
            handleClick={handleCloseReleaseModal}
            hoverColor="#EE3F00"
          />
          <CommonButton
            text="Submit"
            handleClick={handleReleasePaymentSubmit}
            hoverColor="#EE3F00"
            disabled={isButtonEnable}
          />
        </DialogActions>
      </Dialog>
    </>
  );
};

export default CoversheetList;
